import React from 'react';
import {
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core';

import { ExpiringStockBenefitType } from '../models';
import {
    CommonApplianceTarget,
    CommonApplianceType,
    CommonBenefitFormState,
    CommonDiscountType,
    CommonUnitType,
    DEFAULT_COMMON_BENEFIT,
    DEFAULT_EXTENDED_BENEFIT,
    DEFAULT_GRADUATED_DISCOUNT,
    DEFAULT_REDUCE_PRODUCT_AMOUNT,
    DiscountCalculationFormState,
    ExtendedBenefitFormState,
    ExtendedOperator,
    ExtendedReference,
    GraduatedDiscountFormState,
    PositionSelectionFormState,
    ReduceProductAmountFormState,
    SelectionAmountType,
    SelectionFilterType,
    SelectionSort,
} from '../lib/koronaPromotionConfig';

const APPLIANCE_TARGET_LABELS: Record<CommonApplianceTarget, string> = {
    PRODUCT: 'Specific product',
    PRODUCTS_OF_COMMODITY_GROUP: 'Products in commodity group',
    PRODUCTS_WITH_TAG: 'Products with tag',
};

const APPLIANCE_TYPE_LABELS: Record<CommonApplianceType, string> = {
    CHEAPEST_TARGET: 'Cheapest matching item',
    EACH_TARGET: 'Each matching item',
    EQUAL_ON_EACH_TARGET: 'Equal amount on each item',
    MOST_EXPENSIVE_TARGET: 'Most expensive matching item',
    RECEIPT: 'Entire receipt',
};

interface PositionSelectionFieldsProps {
    title: string;
    selection: PositionSelectionFormState;
    onChange: (selection: PositionSelectionFormState) => void;
    allowProductFilter?: boolean;
}

const PositionSelectionFields: React.FC<PositionSelectionFieldsProps> = ({
    title,
    selection,
    onChange,
    allowProductFilter = true,
}) => (
    <>
        <Typography variant="subtitle2" style={{ marginTop: 12 }}>
            {title}
        </Typography>
        <FormControl variant="outlined" fullWidth margin="dense">
            <InputLabel>Count amount type</InputLabel>
            <Select
                label="Count amount type"
                value={selection.countAmount.type}
                onChange={(e) =>
                    onChange({
                        ...selection,
                        countAmount: {
                            type: e.target.value as SelectionAmountType,
                            value: selection.countAmount.value || '1',
                        },
                    })
                }
            >
                <MenuItem value="AMOUNT">Amount</MenuItem>
                <MenuItem value="ALL">All</MenuItem>
            </Select>
        </FormControl>
        {selection.countAmount.type === 'AMOUNT' && (
            <TextField
                label="Count amount"
                type="number"
                value={selection.countAmount.value}
                onChange={(e) =>
                    onChange({
                        ...selection,
                        countAmount: { ...selection.countAmount, value: e.target.value },
                    })
                }
                fullWidth
                margin="dense"
                variant="outlined"
                inputProps={{ min: 0, step: 1 }}
            />
        )}
        <FormControl variant="outlined" fullWidth margin="dense">
            <InputLabel>Discount amount type</InputLabel>
            <Select
                label="Discount amount type"
                value={selection.discountAmount.type}
                onChange={(e) =>
                    onChange({
                        ...selection,
                        discountAmount: {
                            type: e.target.value as SelectionAmountType,
                            value: selection.discountAmount.value || '1',
                        },
                    })
                }
            >
                <MenuItem value="AMOUNT">Amount</MenuItem>
                <MenuItem value="ALL">All</MenuItem>
            </Select>
        </FormControl>
        {selection.discountAmount.type === 'AMOUNT' && (
            <TextField
                label="Discount amount"
                type="number"
                value={selection.discountAmount.value}
                onChange={(e) =>
                    onChange({
                        ...selection,
                        discountAmount: { ...selection.discountAmount, value: e.target.value },
                    })
                }
                fullWidth
                margin="dense"
                variant="outlined"
                inputProps={{ min: 0, step: 1 }}
            />
        )}
        <FormControl variant="outlined" fullWidth margin="dense">
            <InputLabel>Filter type</InputLabel>
            <Select
                label="Filter type"
                value={selection.filter.type}
                onChange={(e) =>
                    onChange({
                        ...selection,
                        filter: { ...selection.filter, type: e.target.value as SelectionFilterType },
                    })
                }
            >
                {allowProductFilter && <MenuItem value="PRODUCT">Product</MenuItem>}
                <MenuItem value="NONE">None</MenuItem>
                <MenuItem value="TAG">Tag</MenuItem>
                <MenuItem value="COMMODITY_GROUP">Commodity group</MenuItem>
            </Select>
        </FormControl>
        <FormControl variant="outlined" fullWidth margin="dense">
            <InputLabel>Sort</InputLabel>
            <Select
                label="Sort"
                value={selection.sort}
                onChange={(e) => onChange({ ...selection, sort: e.target.value as SelectionSort })}
            >
                <MenuItem value="IRRELEVANT">Irrelevant</MenuItem>
                <MenuItem value="CHEAPEST">Cheapest</MenuItem>
                <MenuItem value="MOST_EXPENSIVE">Most expensive</MenuItem>
            </Select>
        </FormControl>
        <FormControlLabel
            control={
                <Switch
                    checked={selection.filter.not}
                    onChange={(e) =>
                        onChange({
                            ...selection,
                            filter: { ...selection.filter, not: e.target.checked },
                        })
                    }
                    color="primary"
                />
            }
            label="Negate filter"
        />
    </>
);

interface Props {
    benefitType: ExpiringStockBenefitType;
    common: CommonBenefitFormState;
    reduceProductAmount: ReduceProductAmountFormState;
    graduated: GraduatedDiscountFormState;
    extended: ExtendedBenefitFormState;
    onCommonChange: (state: CommonBenefitFormState) => void;
    onReduceProductAmountChange: (state: ReduceProductAmountFormState) => void;
    onGraduatedChange: (state: GraduatedDiscountFormState) => void;
    onExtendedChange: (state: ExtendedBenefitFormState) => void;
}

const BenefitConfigForm: React.FC<Props> = ({
    benefitType,
    common,
    reduceProductAmount,
    graduated,
    extended,
    onCommonChange,
    onReduceProductAmountChange,
    onGraduatedChange,
    onExtendedChange,
}) => {
    if (benefitType === 'COMMON') {
        return (
            <>
                <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                    Discount Settings
                </Typography>
                <FormControl variant="outlined" fullWidth margin="dense">
                    <InputLabel id="common-discount-type-label">Discount mode</InputLabel>
                    <Select
                        labelId="common-discount-type-label"
                        label="Discount mode"
                        value={common.discountType}
                        onChange={(e) =>
                            onCommonChange({ ...common, discountType: e.target.value as CommonDiscountType })
                        }
                    >
                        <MenuItem value="DISCOUNT">Discount</MenuItem>
                        <MenuItem value="FIXED_PRICE">Fixed price</MenuItem>
                    </Select>
                </FormControl>
                <FormControl variant="outlined" fullWidth margin="dense">
                    <InputLabel id="common-unit-type-label">Unit</InputLabel>
                    <Select
                        labelId="common-unit-type-label"
                        label="Unit"
                        value={common.unitType}
                        onChange={(e) => onCommonChange({ ...common, unitType: e.target.value as CommonUnitType })}
                    >
                        <MenuItem value="PERCENTAGE">Percentage (%)</MenuItem>
                        <MenuItem value="FIXED">Fixed amount ($)</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    label={common.unitType === 'PERCENTAGE' ? 'Discount value (%)' : 'Discount value ($)'}
                    type="number"
                    value={common.value}
                    onChange={(e) => onCommonChange({ ...common, value: e.target.value })}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    inputProps={{ min: 0, step: common.unitType === 'PERCENTAGE' ? 1 : 0.01 }}
                />
                <FormControl variant="outlined" fullWidth margin="dense">
                    <InputLabel id="common-appliance-target-label">Applies to</InputLabel>
                    <Select
                        labelId="common-appliance-target-label"
                        label="Applies to"
                        value={common.applianceTarget}
                        onChange={(e) =>
                            onCommonChange({ ...common, applianceTarget: e.target.value as CommonApplianceTarget })
                        }
                    >
                        {Object.entries(APPLIANCE_TARGET_LABELS).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl variant="outlined" fullWidth margin="dense">
                    <InputLabel id="common-appliance-type-label">Application</InputLabel>
                    <Select
                        labelId="common-appliance-type-label"
                        label="Application"
                        value={common.applianceType}
                        onChange={(e) =>
                            onCommonChange({ ...common, applianceType: e.target.value as CommonApplianceType })
                        }
                    >
                        {Object.entries(APPLIANCE_TYPE_LABELS).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Max iterations (optional)"
                    type="number"
                    value={common.maxIterations}
                    onChange={(e) => onCommonChange({ ...common, maxIterations: e.target.value })}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    helperText="How many times the discount can apply per receipt."
                    inputProps={{ min: 1 }}
                />
            </>
        );
    }

    if (benefitType === 'REDUCE_PRODUCT_AMOUNT') {
        return (
            <>
                <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                    Price Reduction
                </Typography>
                <TextField
                    label="Reduce price by ($)"
                    type="number"
                    value={reduceProductAmount.reductionValue}
                    onChange={(e) => onReduceProductAmountChange({ reductionValue: e.target.value })}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    helperText="Dollar amount subtracted from the product price. Product is set when creating a tracker."
                    inputProps={{ min: 0, step: 0.01 }}
                />
            </>
        );
    }

    if (benefitType === 'GRADUATED_DISCOUNT') {
        return (
            <>
                <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                    Graduated Discount Layer
                </Typography>
                <TextField
                    label="Korona layer UUID"
                    value={graduated.layerId}
                    onChange={(e) => onGraduatedChange({ ...graduated, layerId: e.target.value })}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    helperText="UUID of an existing graduated discount layer in Korona Cloud."
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={graduated.exclusive}
                            onChange={(e) => onGraduatedChange({ ...graduated, exclusive: e.target.checked })}
                            color="primary"
                        />
                    }
                    label="Exclusive layer"
                />
            </>
        );
    }

    if (benefitType === 'EXTENDED') {
        const calc: DiscountCalculationFormState =
            extended.calculations[0] || DEFAULT_EXTENDED_BENEFIT.calculations[0];

        const updateCalc = (patch: Partial<DiscountCalculationFormState>) => {
            onExtendedChange({
                ...extended,
                calculations: [{ ...calc, ...patch }],
            });
        };

        return (
            <>
                <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                    Extended Discount
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={extended.exclusive}
                            onChange={(e) => onExtendedChange({ ...extended, exclusive: e.target.checked })}
                            color="primary"
                        />
                    }
                    label="Exclusive layer"
                />
                <PositionSelectionFields
                    title="Position selection (items that qualify)"
                    selection={extended.positionSelection}
                    onChange={(positionSelection) => onExtendedChange({ ...extended, positionSelection })}
                />
                <Typography variant="subtitle2" style={{ marginTop: 16 }}>
                    Discount calculation
                </Typography>
                <FormControl variant="outlined" fullWidth margin="dense">
                    <InputLabel id="extended-operator-label">Operator</InputLabel>
                    <Select
                        labelId="extended-operator-label"
                        label="Operator"
                        value={calc.operator}
                        onChange={(e) => updateCalc({ operator: e.target.value as ExtendedOperator })}
                    >
                        <MenuItem value="PERCENTAGE">Percentage</MenuItem>
                        <MenuItem value="MINUS">Subtract amount</MenuItem>
                        <MenuItem value="EQUALS">Set equal to</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    label="Operand"
                    type="number"
                    value={calc.operand}
                    onChange={(e) => updateCalc({ operand: e.target.value })}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    inputProps={{ min: 0, step: 0.01 }}
                />
                <FormControl variant="outlined" fullWidth margin="dense">
                    <InputLabel id="extended-reference-label">Reference</InputLabel>
                    <Select
                        labelId="extended-reference-label"
                        label="Reference"
                        value={calc.reference}
                        onChange={(e) => updateCalc({ reference: e.target.value as ExtendedReference })}
                    >
                        <MenuItem value="SUB_SELECTION">Sub-selection</MenuItem>
                        <MenuItem value="SELECTION">Selection</MenuItem>
                        <MenuItem value="RECEIPT">Receipt</MenuItem>
                        <MenuItem value="REMAINDER">Remainder</MenuItem>
                    </Select>
                </FormControl>
                <FormControl variant="outlined" fullWidth margin="dense">
                    <InputLabel id="extended-sum-reference-label">Sum reference</InputLabel>
                    <Select
                        labelId="extended-sum-reference-label"
                        label="Sum reference"
                        value={calc.sumReference}
                        onChange={(e) => updateCalc({ sumReference: e.target.value as ExtendedReference })}
                    >
                        <MenuItem value="SUB_SELECTION">Sub-selection</MenuItem>
                        <MenuItem value="SELECTION">Selection</MenuItem>
                        <MenuItem value="RECEIPT">Receipt</MenuItem>
                        <MenuItem value="REMAINDER">Remainder</MenuItem>
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={
                        <Switch
                            checked={calc.receiptDiscount}
                            onChange={(e) => updateCalc({ receiptDiscount: e.target.checked })}
                            color="primary"
                        />
                    }
                    label="Apply as receipt discount"
                />
                <PositionSelectionFields
                    title="Calculation position selection"
                    selection={calc.positionSelection}
                    onChange={(positionSelection) => updateCalc({ positionSelection })}
                    allowProductFilter={false}
                />
            </>
        );
    }

    return null;
};

export {
    DEFAULT_COMMON_BENEFIT,
    DEFAULT_EXTENDED_BENEFIT,
    DEFAULT_GRADUATED_DISCOUNT,
    DEFAULT_REDUCE_PRODUCT_AMOUNT,
};

export default BenefitConfigForm;
